import { AuthMessage, AuthMessageType } from '@playcastdotio/Messaging';
import { setIsReady } from '..';
import { joinIdState, profileState } from '../../../stores/lobbyStores';
import { queryInvitation } from './invitationMessage';
import { authStore, joinState } from '../../../stores/globalStores';
import { queryOwnerAliases, queryProfile, queryProfileInvitations } from './profileMessage';
import { IdentityPlatform } from '@playcastdotio/CoreTypes';
import { requestQuotaLimits } from './outboundMessage';
import { onAuthSuccess } from './conectionMessage';
import { queryUserEventStatus } from '../../userEvents';

// Track the last time we requested profile data to implement cooldown
let lastProfileRequestTime = 0;
const PROFILE_REQUEST_COOLDOWN = 5000; // 5 seconds

// Helper function to get profile info from URL
const getProfileInfoFromUrl = () => {
  // Check if URL contains profile paths
  const path = window.location.pathname;
  const profilePaths = {
    'p/': IdentityPlatform.CLERK,
    'pt/': IdentityPlatform.TWITCH,
    'pd/': IdentityPlatform.DISCORD,
  };

  for (const [pathPrefix, platform] of Object.entries(profilePaths)) {
    if (path.includes(`/${pathPrefix}`)) {
      const parts = path.split(`/${pathPrefix}`);
      if (parts.length > 1) {
        // Extract alias from path
        const alias = parts[1].split('/')[0];
        return { alias, platform };
      }
    }
  }

  return null;
};

// Helper function to request profile data with cooldown
const requestProfileDataWithCooldown = (alias: string, platform: IdentityPlatform, sentUpgrade: boolean) => {
  const now = Date.now();
  if (now - lastProfileRequestTime < PROFILE_REQUEST_COOLDOWN) {
    console.log('Profile request on cooldown, skipping request');
    return;
  }

  console.log('Re-requesting profile on reconnection:', alias);
  lastProfileRequestTime = now;

  // If we just sent an upgrade, wait a bit before requesting profile data
  const delay = sentUpgrade ? 500 : 0;
  setTimeout(() => {
    queryProfile(alias, platform);
    queryProfileInvitations(alias, platform);
  }, delay);
};

export const handleAuthMessage = async (message: AuthMessage) => {
  console.log('Auth message:', message);
  switch (message.type) {
    case AuthMessageType.AUTH_SUCCESS: {
      console.log('Auth success');
      setIsReady(true);
      // Call onAuthSuccess to handle potential auth upgrade
      const sentUpgrade = onAuthSuccess();

      if (authStore.currentState.playerAuthId && !sentUpgrade) {
        // queryOwnerAliases();
        requestQuotaLimits();

        // Query user events to get mailing list status and other user preferences
        queryUserEventStatus().catch((error) => {
          console.warn('Failed to query user events on auth success:', error);
        });
      }

      // Only query if we know we arent upgrading
      const joinId = joinIdState.currentState;
      if (joinId && !sentUpgrade) {
        // Wait for "loaded" to be true.
        const isLoaded = await new Promise((resolve) => {
          const loaded = authStore.currentState.loaded;
          if (loaded) {
            resolve(true);
          } else {
            const listener = authStore.addListener((state) => {
              if (state.loaded) {
                resolve(true);
                authStore.removeListener(listener);
              }
            });
            setTimeout(() => {
              authStore.removeListener(listener);
              resolve(false);
            }, 1000);
          }
        });
        if (isLoaded) {
          queryInvitation(joinId, joinState.currentState.platform);
        }
      }

      // Re-request profile and invitations if we're on a profile page
      const profileInfo = getProfileInfoFromUrl();
      if (profileInfo && !profileState.currentState.isLoading) {
        console.log('AUTH_UPGRADE_SUCCESS: Profile not loading, requesting profile data');
        requestProfileDataWithCooldown(profileInfo.alias, profileInfo.platform, sentUpgrade);
      } else if (profileInfo && profileState.currentState.isLoading) {
        console.log('AUTH_UPGRADE_SUCCESS: Profile already loading, skipping duplicate request');
      }
      break;
    }
    case AuthMessageType.AUTH_UPGRADE_SUCCESS: {
      console.log('Auth upgrade success');
      setIsReady(true);
      setTimeout(() => {
        const joinId = joinIdState.currentState;
        if (joinId) {
          console.warn('Query: auth upgrade success');
          queryInvitation(joinId, joinState.currentState.platform);
        }
      }, 1000);
      // queryOwnerAliases();

      requestQuotaLimits();

      // Query user events to get mailing list status and other user preferences
      queryUserEventStatus().catch((error) => {
        console.warn('Failed to query user events on auth upgrade:', error);
      });

      // Re-request profile and invitations if we're on a profile page
      const profileInfo = getProfileInfoFromUrl();
      if (profileInfo) {
        requestProfileDataWithCooldown(profileInfo.alias, profileInfo.platform, false);
      }
      break;
    }
    case AuthMessageType.AUTH_PROFILE_READY: {
      console.log('Auth profile ready');
      queryOwnerAliases();
      break;
    }
  }
};

